import { Transaction, RecurringTransaction } from '@/types/transaction';
import { BankAccount, PlaidItem } from '@/types/plaid';

// Local type for mock Plaid Item data including logo
interface MockPlaidItem extends PlaidItem {
  logoUrl?: string;
}

// Placeholder budget data type
interface BudgetCategoryData {
  id: string;
  name: string;
  icon: string;
  allocated: number;
  spent: number;
  color?: string;
}

// --- MOCK RECURRING TRANSACTIONS --- Regenerated
export const mockRecurringTransactions: RecurringTransaction[] = [
  {
    stream_id: "netflix-stream-mock",
    account_id: "plaid-credit-acc-id", // Associated with mock credit card
    plaidItemId: "mock-item-id-2",
    merchant_name: "Netflix",
    logo_url: 'https://logo.clearbit.com/netflix.com',
    description: "NETFLIX.COM 866-579-7172",
    personal_finance_category: {
      primary: "SERVICE",
      detailed: "ENTERTAINMENT_SUBSCRIPTION"
    },
    frequency: "MONTHLY",
    status: 'ACTIVE',
    last_date: "2025-04-15",
    last_amount: { amount: -15.99, iso_currency_code: "USD", unofficial_currency_code: null },
    average_amount: { amount: -15.99, iso_currency_code: "USD", unofficial_currency_code: null },
    predicted_next_date: "2025-05-15",
    is_active: true
  },
  {
    stream_id: "spotify-stream-mock",
    account_id: "plaid-credit-acc-id", // Associated with mock credit card
    plaidItemId: "mock-item-id-2",
    merchant_name: "Spotify",
    logo_url: 'https://logo.clearbit.com/spotify.com',
    description: "SPOTIFY* P1X2Y3Z4",
    personal_finance_category: {
      primary: "SERVICE",
      detailed: "ENTERTAINMENT_SUBSCRIPTION"
    },
    frequency: "MONTHLY",
    status: 'ACTIVE',
    last_date: "2025-04-10",
    last_amount: { amount: -10.99, iso_currency_code: "USD", unofficial_currency_code: null },
    average_amount: { amount: -10.99, iso_currency_code: "USD", unofficial_currency_code: null },
    predicted_next_date: "2025-05-10",
    is_active: true
  },
  {
    stream_id: "gym-stream-mock",
    account_id: "plaid-checking-acc-id", // Associated with mock checking
    plaidItemId: "mock-item-id-1",
    merchant_name: "Planet Fitness",
    logo_url: 'https://logo.clearbit.com/planetfitness.com',
    description: "PF* MEMBERSHIP 9876",
    personal_finance_category: {
      primary: "SERVICE",
      detailed: "GYMS_AND_FITNESS_CENTERS"
    },
    frequency: "MONTHLY",
    status: 'ACTIVE',
    last_date: "2025-04-05",
    last_amount: { amount: -25.00, iso_currency_code: "USD", unofficial_currency_code: null },
    average_amount: { amount: -25.00, iso_currency_code: "USD", unofficial_currency_code: null },
    predicted_next_date: "2025-05-05",
    is_active: true
  },
  {
    stream_id: "insurance-stream-mock",
    account_id: "plaid-checking-acc-id", // Associated with mock checking
    plaidItemId: "mock-item-id-1",
    merchant_name: "Geico",
    logo_url: 'https://logo.clearbit.com/geico.com',
    description: "GEICO AUTOPAY 1A2B3C",
    personal_finance_category: {
      primary: "GENERAL_SERVICES",
      detailed: "INSURANCE"
    },
    frequency: "MONTHLY",
    status: 'ACTIVE',
    last_date: "2025-04-20",
    last_amount: { amount: -125.50, iso_currency_code: "USD", unofficial_currency_code: null },
    average_amount: { amount: -125.50, iso_currency_code: "USD", unofficial_currency_code: null },
    predicted_next_date: "2025-05-20",
    is_active: true
  },
  {
    stream_id: "rent-stream-mock",
    account_id: "plaid-checking-acc-id", // Associated with mock checking
    plaidItemId: "mock-item-id-1",
    merchant_name: null, // Often no merchant for rent/mortgage ACH
    logo_url: null,
    description: "RENT ACH - PROPERTY MGMT CO",
    personal_finance_category: {
      primary: "RENT_AND_UTILITIES",
      detailed: "RENT"
    },
    frequency: "MONTHLY",
    status: 'ACTIVE',
    last_date: "2025-04-01",
    last_amount: { amount: -1800.00, iso_currency_code: "USD", unofficial_currency_code: null },
    average_amount: { amount: -1800.00, iso_currency_code: "USD", unofficial_currency_code: null },
    predicted_next_date: "2025-05-01",
    is_active: true
  },
  {
    stream_id: "paycheck-stream-mock",
    account_id: "plaid-checking-acc-id", // Associated with mock checking
    plaidItemId: "mock-item-id-1",
    merchant_name: "Company Payroll",
    logo_url: null, // Often no logo for direct deposits
    description: "DIRECT DEPOSIT - COMPANY NAME",
    personal_finance_category: {
      primary: "INCOME",
      detailed: "INCOME_WAGES"
    },
    frequency: "BI_WEEKLY", // Or SEMI_MONTHLY, WEEKLY etc.
    status: 'ACTIVE',
    last_date: "2025-04-18",
    last_amount: { amount: 2500.00, iso_currency_code: "USD", unofficial_currency_code: null }, // Positive amount for income
    average_amount: { amount: 2500.00, iso_currency_code: "USD", unofficial_currency_code: null },
    predicted_next_date: "2025-05-02",
    is_active: true
  }
];

// --- MOCK TRANSACTION DATA ---
export const mockTransactions: Transaction[] = [
  // --- NEW TRANSACTIONS (Nov 2024 - Feb 2025) ---
  {
    id: 'mock-tx-34',
    userId: 'mock-user-id',
    account_id: 'plaid-checking-acc-id',
    plaidItemId: 'mock-item-id-1',
    transaction_id: 'plaid-tx-id-34',
    name: 'Utility Bill - Water',
    amount: -65.70,
    date: '2025-02-20',
    iso_currency_code: 'USD',
    category: ['RENT_AND_UTILITIES', 'Water'], // Updated
    pending: false,
    merchant_name: 'City Water Dept',
    logo_url: null,
    payment_channel: 'online',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'mock-tx-35',
    userId: 'mock-user-id',
    account_id: 'plaid-credit-acc-id',
    plaidItemId: 'mock-item-id-2',
    transaction_id: 'plaid-tx-id-35',
    name: 'Streaming Service - Hulu',
    amount: -14.99,
    date: '2025-02-18',
    iso_currency_code: 'USD',
    category: ['SERVICES', 'Subscription'], // Updated
    pending: false,
    merchant_name: 'Hulu',
    logo_url: 'https://logo.clearbit.com/hulu.com',
    payment_channel: 'online',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'mock-tx-36',
    userId: 'mock-user-id',
    account_id: 'plaid-checking-acc-id',
    plaidItemId: 'mock-item-id-1',
    transaction_id: 'plaid-tx-id-36',
    name: 'Lunch Takeout',
    amount: -18.25,
    date: '2025-02-15',
    iso_currency_code: 'USD',
    category: ['FOOD_AND_DRINK', 'Restaurants', 'Fast Food'], // Updated
    pending: false,
    merchant_name: 'Quick Eats Deli',
    logo_url: null,
    payment_channel: 'in store',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'mock-tx-37',
    userId: 'mock-user-id',
    account_id: 'plaid-credit-acc-id',
    plaidItemId: 'mock-item-id-2',
    transaction_id: 'plaid-tx-id-37',
    name: 'Electronics Store',
    amount: -250.00,
    date: '2025-02-12',
    iso_currency_code: 'USD',
    category: ['SHOPPING', 'Electronics'], // Updated
    pending: false,
    merchant_name: 'Best Buy',
    logo_url: 'https://logo.clearbit.com/bestbuy.com',
    payment_channel: 'in store',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'mock-tx-38',
    userId: 'mock-user-id',
    account_id: 'plaid-checking-acc-id',
    plaidItemId: 'mock-item-id-1',
    transaction_id: 'plaid-tx-id-38',
    name: 'Grocery Run',
    amount: -95.40,
    date: '2025-02-10',
    iso_currency_code: 'USD',
    category: ['FOOD_AND_DRINK', 'Groceries'], // Updated
    pending: false,
    merchant_name: 'Safeway',
    logo_url: 'https://logo.clearbit.com/safeway.com',
    payment_channel: 'in store',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'mock-tx-39',
    userId: 'mock-user-id',
    account_id: 'plaid-credit-acc-id',
    plaidItemId: 'mock-item-id-2',
    transaction_id: 'plaid-tx-id-39',
    name: 'Software Subscription - Adobe',
    amount: -52.99,
    date: '2025-02-08',
    iso_currency_code: 'USD',
    category: ['SERVICES', 'Subscription', 'Software'], // Updated
    pending: false,
    merchant_name: 'Adobe',
    logo_url: 'https://logo.clearbit.com/adobe.com',
    payment_channel: 'online',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'mock-tx-40',
    userId: 'mock-user-id',
    account_id: 'plaid-checking-acc-id',
    plaidItemId: 'mock-item-id-1',
    transaction_id: 'plaid-tx-id-40',
    name: 'Cell Phone Bill',
    amount: -85.00,
    date: '2025-02-05',
    iso_currency_code: 'USD',
    category: ['RENT_AND_UTILITIES', 'Mobile Phone'], // Updated
    pending: false,
    merchant_name: 'Verizon Wireless',
    logo_url: 'https://logo.clearbit.com/verizon.com',
    payment_channel: 'online',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'mock-tx-41',
    userId: 'mock-user-id',
    account_id: 'plaid-credit-acc-id',
    plaidItemId: 'mock-item-id-2',
    transaction_id: 'plaid-tx-id-41',
    name: 'Department Store',
    amount: -115.30,
    date: '2025-02-02',
    iso_currency_code: 'USD',
    category: ['SHOPPING', 'Department Stores'], // Updated
    pending: false,
    merchant_name: 'Macy\'s',
    logo_url: 'https://logo.clearbit.com/macys.com',
    payment_channel: 'in store',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'mock-tx-42',
    userId: 'mock-user-id',
    account_id: 'plaid-checking-acc-id',
    plaidItemId: 'mock-item-id-1',
    transaction_id: 'plaid-tx-id-42',
    name: 'Paycheck Deposit',
    amount: 2500.00,
    date: '2025-01-31',
    iso_currency_code: 'USD',
    category: ['INCOME', 'Wages'], // Updated
    pending: false,
    merchant_name: 'Company Payroll',
    logo_url: null,
    payment_channel: 'other',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
   {
    id: 'mock-tx-43',
    userId: 'mock-user-id',
    account_id: 'plaid-credit-acc-id',
    plaidItemId: 'mock-item-id-2',
    transaction_id: 'plaid-tx-id-43',
    name: 'Gas Fill Up',
    amount: -51.80,
    date: '2025-01-28',
    iso_currency_code: 'USD',
    category: ['TRAVEL', 'Gas Stations'], // Updated
    pending: false,
    merchant_name: 'ExxonMobil',
    logo_url: 'https://logo.clearbit.com/exxonmobil.com',
    payment_channel: 'in store',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'mock-tx-44',
    userId: 'mock-user-id',
    account_id: 'plaid-checking-acc-id',
    plaidItemId: 'mock-item-id-1',
    transaction_id: 'plaid-tx-id-44',
    name: 'Concert Tickets',
    amount: -180.00,
    date: '2025-01-25',
    iso_currency_code: 'USD',
    category: ['ENTERTAINMENT', 'Music'], // Updated
    pending: false,
    merchant_name: 'Ticketmaster',
    logo_url: 'https://logo.clearbit.com/ticketmaster.com',
    payment_channel: 'online',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'mock-tx-45',
    userId: 'mock-user-id',
    account_id: 'plaid-credit-acc-id',
    plaidItemId: 'mock-item-id-2',
    transaction_id: 'plaid-tx-id-45',
    name: 'Dinner with Friends',
    amount: -85.60,
    date: '2025-01-22',
    iso_currency_code: 'USD',
    category: ['FOOD_AND_DRINK', 'Restaurants'], // Updated
    pending: false,
    merchant_name: 'Neighborhood Grill',
    logo_url: null,
    payment_channel: 'in store',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'mock-tx-46',
    userId: 'mock-user-id',
    account_id: 'plaid-checking-acc-id',
    plaidItemId: 'mock-item-id-1',
    transaction_id: 'plaid-tx-id-46',
    name: 'Gym Membership',
    amount: -25.00,
    date: '2025-01-20',
    iso_currency_code: 'USD',
    category: ['SERVICES', 'Gyms and Fitness Centers'], // Updated
    pending: false,
    merchant_name: 'Planet Fitness',
    logo_url: 'https://logo.clearbit.com/planetfitness.com',
    payment_channel: 'online',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'mock-tx-47',
    userId: 'mock-user-id',
    account_id: 'plaid-savings-acc-id',
    plaidItemId: 'mock-item-id-1',
    transaction_id: 'plaid-tx-id-47',
    name: 'Interest Earned',
    amount: 11.50,
    date: '2025-01-31',
    iso_currency_code: 'USD',
    category: ['TRANSFER_IN', 'Interest'], // Updated
    pending: false,
    merchant_name: null,
    logo_url: 'https://seeklogo.com/images/R/rbc-royal-bank-of-canada-logo-D0A1D244CF-seeklogo.com.png',
    payment_channel: 'other',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'mock-tx-48',
    userId: 'mock-user-id',
    account_id: 'plaid-credit-acc-id',
    plaidItemId: 'mock-item-id-2',
    transaction_id: 'plaid-tx-id-48',
    name: 'Ride Share - Lyft',
    amount: -19.75,
    date: '2025-01-18',
    iso_currency_code: 'USD',
    category: ['TRAVEL', 'Ride Share'], // Updated
    pending: false,
    merchant_name: 'Lyft',
    logo_url: 'https://logo.clearbit.com/lyft.com',
    payment_channel: 'online',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'mock-tx-49',
    userId: 'mock-user-id',
    account_id: 'plaid-checking-acc-id',
    plaidItemId: 'mock-item-id-1',
    transaction_id: 'plaid-tx-id-49',
    name: 'Hardware Store',
    amount: -45.30,
    date: '2025-01-15',
    iso_currency_code: 'USD',
    category: ['SHOPPING', 'Hardware Store'], // Updated
    pending: false,
    merchant_name: 'Ace Hardware',
    logo_url: 'https://logo.clearbit.com/acehardware.com',
    payment_channel: 'in store',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'mock-tx-50',
    userId: 'mock-user-id',
    account_id: 'plaid-checking-acc-id',
    plaidItemId: 'mock-item-id-1',
    transaction_id: 'plaid-tx-id-50',
    name: 'Transfer to Investment Account',
    amount: -200.00,
    date: '2025-01-10',
    iso_currency_code: 'USD',
    category: ['TRANSFER_OUT', 'Investment'], // Updated
    pending: false,
    merchant_name: null,
    logo_url: null,
    payment_channel: 'other',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'mock-tx-51',
    userId: 'mock-user-id',
    account_id: 'plaid-credit-acc-id',
    plaidItemId: 'mock-item-id-2',
    transaction_id: 'plaid-tx-id-51',
    name: 'Amazon Purchase',
    amount: -68.90,
    date: '2025-01-07',
    iso_currency_code: 'USD',
    category: ['SHOPPING', 'Digital Purchases'], // Updated
    pending: false,
    merchant_name: 'Amazon',
    logo_url: 'https://logo.clearbit.com/amazon.com',
    payment_channel: 'online',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'mock-tx-52',
    userId: 'mock-user-id',
    account_id: 'plaid-checking-acc-id',
    plaidItemId: 'mock-item-id-1',
    transaction_id: 'plaid-tx-id-52',
    name: 'Doctor Co-pay',
    amount: -30.00,
    date: '2025-01-05',
    iso_currency_code: 'USD',
    category: ['HEALTHCARE', 'Doctors'], // Updated
    pending: false,
    merchant_name: 'Medical Clinic XYZ',
    logo_url: null,
    payment_channel: 'in store',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'mock-tx-53',
    userId: 'mock-user-id',
    account_id: 'plaid-credit-acc-id',
    plaidItemId: 'mock-item-id-2',
    transaction_id: 'plaid-tx-id-53',
    name: 'Coffee Shop',
    amount: -5.50,
    date: '2024-12-28',
    iso_currency_code: 'USD',
    category: ['FOOD_AND_DRINK', 'Coffee Shop'], // Updated
    pending: false,
    merchant_name: 'Starbucks',
    logo_url: 'https://logo.clearbit.com/starbucks.com',
    location: { lat: 45.4210, lon: -75.6980, address: null, city: 'Ottawa', region: 'ON', postal_code: null, country: 'CA', store_number: null }, // Added location
    payment_channel: 'in store',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'mock-tx-54',
    userId: 'mock-user-id',
    account_id: 'plaid-checking-acc-id',
    plaidItemId: 'mock-item-id-1',
    transaction_id: 'plaid-tx-id-54',
    name: 'Holiday Gifts',
    amount: -150.00,
    date: '2024-12-20',
    iso_currency_code: 'USD',
    category: ['SHOPPING', 'Gifts'], // Updated
    pending: false,
    merchant_name: 'Various Retailers',
    logo_url: null,
    payment_channel: 'in store',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'mock-tx-55',
    userId: 'mock-user-id',
    account_id: 'plaid-credit-acc-id',
    plaidItemId: 'mock-item-id-2',
    transaction_id: 'plaid-tx-id-55',
    name: 'Travel Booking - Hotel',
    amount: -450.80,
    date: '2024-12-15',
    iso_currency_code: 'USD',
    category: ['TRAVEL', 'Hotels'], // Updated
    pending: false,
    merchant_name: 'Marriott Hotels',
    logo_url: 'https://logo.clearbit.com/marriott.com',
    payment_channel: 'online',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'mock-tx-56',
    userId: 'mock-user-id',
    account_id: 'plaid-checking-acc-id',
    plaidItemId: 'mock-item-id-1',
    transaction_id: 'plaid-tx-id-56',
    name: 'Rent Payment',
    amount: -1800.00,
    date: '2024-12-01',
    iso_currency_code: 'USD',
    category: ['RENT_AND_UTILITIES', 'Rent'], // Updated
    pending: false,
    merchant_name: 'Property Management Co',
    logo_url: null,
    payment_channel: 'online',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'mock-tx-57',
    userId: 'mock-user-id',
    account_id: 'plaid-credit-acc-id',
    plaidItemId: 'mock-item-id-2',
    transaction_id: 'plaid-tx-id-57',
    name: 'Grocery Delivery',
    amount: -110.20,
    date: '2024-11-25',
    iso_currency_code: 'USD',
    category: ['FOOD_AND_DRINK', 'Groceries', 'Delivery'], // Updated
    pending: false,
    merchant_name: 'Instacart',
    logo_url: 'https://logo.clearbit.com/instacart.com',
    payment_channel: 'online',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'mock-tx-58',
    userId: 'mock-user-id',
    account_id: 'plaid-checking-acc-id',
    plaidItemId: 'mock-item-id-1',
    transaction_id: 'plaid-tx-id-58',
    name: 'Donation to Charity',
    amount: -50.00,
    date: '2024-11-15',
    iso_currency_code: 'USD',
    category: ['SERVICES', 'Charity'], // Updated (Mapped General Services -> SERVICES)
    pending: false,
    merchant_name: 'Charity Foundation',
    logo_url: null,
    payment_channel: 'online',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },

  // --- PREVIOUSLY ADDED TRANSACTIONS (Feb-Apr 2025) --- 
  {
    id: 'mock-tx-14',
    userId: 'mock-user-id',
    account_id: 'plaid-checking-acc-id',
    plaidItemId: 'mock-item-id-1',
    transaction_id: 'plaid-tx-id-14',
    name: 'LOCAL CAFE',
    amount: -8.50,
    date: '2025-04-24',
    iso_currency_code: 'USD',
    category: ['FOOD_AND_DRINK', 'Coffee Shop'], // Updated
    pending: false,
    merchant_name: 'Local Cafe',
    logo_url: null,
    location: { lat: 45.4235, lon: -75.6965, address: null, city: 'Ottawa', region: 'ON', postal_code: null, country: 'CA', store_number: null }, // Added location
    payment_channel: 'in store',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'mock-tx-15',
    userId: 'mock-user-id',
    account_id: 'plaid-credit-acc-id',
    plaidItemId: 'mock-item-id-2',
    transaction_id: 'plaid-tx-id-15',
    name: 'ONLINE BOOKSTORE',
    amount: -25.99,
    date: '2025-04-23',
    iso_currency_code: 'USD',
    category: ['SHOPPING', 'Books and Supplies'], // Updated
    pending: false,
    merchant_name: 'Online Bookstore',
    logo_url: 'https://logo.clearbit.com/barnesandnoble.com',
    payment_channel: 'online',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
    {
    id: 'mock-tx-16',
    userId: 'mock-user-id',
    account_id: 'plaid-checking-acc-id',
    plaidItemId: 'mock-item-id-1',
    transaction_id: 'plaid-tx-id-16',
    name: 'GAS STATION CORNER MART',
    amount: -48.15,
    date: '2025-04-20',
    iso_currency_code: 'USD',
    category: ['TRAVEL', 'Gas Stations'], // Updated
    pending: false,
    merchant_name: 'Corner Mart Gas',
    logo_url: 'https://logo.clearbit.com/chevron.com',
    location: { lat: 45.4188, lon: -75.6910, address: null, city: 'Ottawa', region: 'ON', postal_code: null, country: 'CA', store_number: null }, // Added location
    payment_channel: 'in store',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'mock-tx-17',
    userId: 'mock-user-id',
    account_id: 'plaid-credit-acc-id',
    plaidItemId: 'mock-item-id-2',
    transaction_id: 'plaid-tx-id-17',
    name: 'MOVIE TICKET PURCHASE',
    amount: -32.00,
    date: '2025-04-18',
    iso_currency_code: 'USD',
    category: ['ENTERTAINMENT', 'Movies'], // Updated
    pending: false,
    merchant_name: 'Cinema Plex',
    logo_url: 'https://logo.clearbit.com/amctheatres.com',
    location: { lat: 45.4250, lon: -75.7005, address: null, city: 'Ottawa', region: 'ON', postal_code: null, country: 'CA', store_number: null }, // Added location
    payment_channel: 'online', // Even if online, associate with a place
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'mock-tx-18',
    userId: 'mock-user-id',
    account_id: 'plaid-checking-acc-id',
    plaidItemId: 'mock-item-id-1',
    transaction_id: 'plaid-tx-id-18',
    name: 'Pharmacy Purchase',
    amount: -18.75,
    date: '2025-04-15',
    iso_currency_code: 'USD',
    category: ['HEALTHCARE', 'Pharmacy'], // Updated
    pending: false,
    merchant_name: 'CVS Pharmacy',
    logo_url: 'https://logo.clearbit.com/cvs.com',
    location: { lat: 45.4205, lon: -75.6940, address: null, city: 'Ottawa', region: 'ON', postal_code: null, country: 'CA', store_number: null }, // Added location
    payment_channel: 'in store',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'mock-tx-19',
    userId: 'mock-user-id',
    account_id: 'plaid-credit-acc-id',
    plaidItemId: 'mock-item-id-2',
    transaction_id: 'plaid-tx-id-19',
    name: 'MUSIC STREAMING SERVICE',
    amount: -9.99,
    date: '2025-04-12',
    iso_currency_code: 'USD',
    category: ['SERVICES', 'Subscription'], // Updated
    pending: false,
    merchant_name: 'Apple Music',
    logo_url: 'https://logo.clearbit.com/apple.com',
    payment_channel: 'online',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'mock-tx-20',
    userId: 'mock-user-id',
    account_id: 'plaid-checking-acc-id',
    plaidItemId: 'mock-item-id-1',
    transaction_id: 'plaid-tx-id-20',
    name: 'ATM WITHDRAWAL',
    amount: -100.00,
    date: '2025-04-10',
    iso_currency_code: 'USD',
    category: ['TRANSFER_OUT', 'Withdrawal', 'ATM'], // Updated
    pending: false,
    merchant_name: 'ATM Network',
    logo_url: null,
    payment_channel: 'other',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'mock-tx-21',
    userId: 'mock-user-id',
    account_id: 'plaid-credit-acc-id',
    plaidItemId: 'mock-item-id-2',
    transaction_id: 'plaid-tx-id-21',
    name: 'RESTAURANT DINNER',
    amount: -65.50,
    date: '2025-04-08',
    iso_currency_code: 'USD',
    category: ['FOOD_AND_DRINK', 'Restaurants'], // Updated
    pending: false,
    merchant_name: 'Italian Place',
    logo_url: null,
    location: { lat: 45.4222, lon: -75.6991, address: null, city: 'Ottawa', region: 'ON', postal_code: null, country: 'CA', store_number: null }, // Added location
    payment_channel: 'in store',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'mock-tx-22',
    userId: 'mock-user-id',
    account_id: 'plaid-checking-acc-id',
    plaidItemId: 'mock-item-id-1',
    transaction_id: 'plaid-tx-id-22',
    name: 'INTERNET BILL PAYMENT',
    amount: -70.00,
    date: '2025-04-05',
    iso_currency_code: 'USD',
    category: ['RENT_AND_UTILITIES', 'Internet'], // Updated
    pending: false,
    merchant_name: 'Comcast',
    logo_url: 'https://logo.clearbit.com/comcast.com',
    payment_channel: 'online',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'mock-tx-23',
    userId: 'mock-user-id',
    account_id: 'plaid-credit-acc-id',
    plaidItemId: 'mock-item-id-2',
    transaction_id: 'plaid-tx-id-23',
    name: 'HOME GOODS STORE',
    amount: -120.10,
    date: '2025-04-02',
    iso_currency_code: 'USD',
    category: ['SHOPPING', 'Home Improvement'], // Updated
    pending: false,
    merchant_name: 'Home Depot',
    logo_url: 'https://logo.clearbit.com/homedepot.com',
    location: { lat: 45.4150, lon: -75.7050, address: null, city: 'Ottawa', region: 'ON', postal_code: null, country: 'CA', store_number: null }, // Added location
    payment_channel: 'in store',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
   {
    id: 'mock-tx-24',
    userId: 'mock-user-id',
    account_id: 'plaid-checking-acc-id',
    plaidItemId: 'mock-item-id-1',
    transaction_id: 'plaid-tx-id-24',
    name: 'PARKING GARAGE FEE',
    amount: -15.00,
    date: '2025-03-28',
    iso_currency_code: 'USD',
    category: ['TRAVEL', 'Parking'], // Updated
    pending: false,
    merchant_name: 'City Parking',
    logo_url: null,
    location: { lat: 45.4240, lon: -75.6978, address: null, city: 'Ottawa', region: 'ON', postal_code: null, country: 'CA', store_number: null }, // Added location
    payment_channel: 'in store',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'mock-tx-26',
    userId: 'mock-user-id',
    account_id: 'plaid-checking-acc-id',
    plaidItemId: 'mock-item-id-1',
    transaction_id: 'plaid-tx-id-26',
    name: 'PAYPAL PURCHASE *SellerName',
    amount: -35.00,
    date: '2025-03-22',
    iso_currency_code: 'USD',
    category: ['TRANSFER_OUT', 'Payment', 'PayPal'], // Updated
    pending: false,
    merchant_name: 'PayPal',
    logo_url: 'https://logo.clearbit.com/paypal.com',
    payment_channel: 'online',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'mock-tx-27',
    userId: 'mock-user-id',
    account_id: 'plaid-savings-acc-id',
    plaidItemId: 'mock-item-id-1',
    transaction_id: 'plaid-tx-id-27',
    name: 'MONTHLY INTEREST EARNED',
    amount: 12.34,
    date: '2025-03-31',
    iso_currency_code: 'USD',
    category: ['TRANSFER_IN', 'Interest'], // Updated
    pending: false,
    merchant_name: null,
    logo_url: null,
    payment_channel: 'other',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'mock-tx-28',
    userId: 'mock-user-id',
    account_id: 'plaid-credit-acc-id',
    plaidItemId: 'mock-item-id-2',
    transaction_id: 'plaid-tx-id-28',
    name: 'FOOD DELIVERY SERVICE',
    amount: -42.80,
    date: '2025-03-18',
    iso_currency_code: 'USD',
    category: ['FOOD_AND_DRINK', 'Restaurants', 'Delivery'], // Updated
    pending: false,
    merchant_name: 'DoorDash',
    logo_url: 'https://logo.clearbit.com/doordash.com',
    payment_channel: 'online',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'mock-tx-29',
    userId: 'mock-user-id',
    account_id: 'plaid-checking-acc-id',
    plaidItemId: 'mock-item-id-1',
    transaction_id: 'plaid-tx-id-29',
    name: 'PET STORE SUPPLIES',
    amount: -55.25,
    date: '2025-03-15',
    iso_currency_code: 'USD',
    category: ['SHOPPING', 'Pet Supplies'], // Updated
    pending: false,
    merchant_name: 'PetSmart',
    logo_url: 'https://logo.clearbit.com/petsmart.com',
    payment_channel: 'in store',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'mock-tx-30',
    userId: 'mock-user-id',
    account_id: 'plaid-checking-acc-id',
    plaidItemId: 'mock-item-id-1',
    transaction_id: 'plaid-tx-id-30',
    name: 'TRANSFER TO SAVINGS',
    amount: -300.00,
    date: '2025-03-10',
    iso_currency_code: 'USD',
    category: ['TRANSFER_OUT', 'Withdrawal'], // Updated
    pending: false,
    merchant_name: null,
    logo_url: null,
    payment_channel: 'other',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'mock-tx-31',
    userId: 'mock-user-id',
    account_id: 'plaid-credit-acc-id',
    plaidItemId: 'mock-item-id-2',
    transaction_id: 'plaid-tx-id-31',
    name: 'AIRLINE TICKET',
    amount: -350.90,
    date: '2025-03-05',
    iso_currency_code: 'USD',
    category: ['TRAVEL', 'Airlines'], // Updated
    pending: false,
    merchant_name: 'United Airlines',
    logo_url: 'https://logo.clearbit.com/united.com',
    payment_channel: 'online',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'mock-tx-32',
    userId: 'mock-user-id',
    account_id: 'plaid-checking-acc-id',
    plaidItemId: 'mock-item-id-1',
    transaction_id: 'plaid-tx-id-32',
    name: 'CAR WASH SERVICE',
    amount: -20.00,
    date: '2025-02-28',
    iso_currency_code: 'USD',
    category: ['SERVICES', 'Automotive'], // Updated (Mapped General Services -> SERVICES)
    pending: false,
    merchant_name: 'Speedy Car Wash',
    logo_url: null,
    payment_channel: 'in store',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'mock-tx-33',
    userId: 'mock-user-id',
    account_id: 'plaid-credit-acc-id',
    plaidItemId: 'mock-item-id-2',
    transaction_id: 'plaid-tx-id-33',
    name: 'SPORTING GOODS STORE',
    amount: -75.00,
    date: '2025-02-25',
    iso_currency_code: 'USD',
    category: ['SHOPPING', 'Sporting Goods'], // Updated
    pending: false,
    merchant_name: 'DICK\'s Sporting Goods',
    logo_url: 'https://logo.clearbit.com/dickssportinggoods.com',
    payment_channel: 'in store',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },

  // --- NEWLY ADDED MOCK TRANSACTIONS ---
  {
    id: 'mock-tx-59',
    userId: 'mock-user-id',
    account_id: 'plaid-checking-acc-id',
    plaidItemId: 'mock-item-id-1',
    transaction_id: 'plaid-tx-id-59',
    name: 'Public Transit Pass',
    amount: -75.00,
    date: '2025-03-01',
    iso_currency_code: 'USD',
    category: ['TRAVEL', 'Public Transportation'], // Updated
    pending: false,
    merchant_name: 'City Transit Authority',
    logo_url: null,
    payment_channel: 'online',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'mock-tx-60',
    userId: 'mock-user-id',
    account_id: 'plaid-credit-acc-id',
    plaidItemId: 'mock-item-id-2',
    transaction_id: 'plaid-tx-id-60',
    name: 'Clothing Store',
    amount: -88.45,
    date: '2025-02-14',
    iso_currency_code: 'USD',
    category: ['SHOPPING', 'Clothing'], // Updated
    pending: false,
    merchant_name: 'Old Navy',
    logo_url: 'https://logo.clearbit.com/oldnavy.com',
    payment_channel: 'in store',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
    {
    id: 'mock-tx-61',
    userId: 'mock-user-id',
    account_id: 'plaid-checking-acc-id',
    plaidItemId: 'mock-item-id-1',
    transaction_id: 'plaid-tx-id-61',
    name: 'Venmo Payment - Rent Share',
    amount: -900.00,
    date: '2025-01-02',
    iso_currency_code: 'USD',
    category: ['TRANSFER_OUT', 'Payment', 'Venmo'], // Updated
    pending: false,
    merchant_name: 'Venmo',
    logo_url: 'https://logo.clearbit.com/venmo.com',
    payment_channel: 'online',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
   {
    id: 'mock-tx-62',
    userId: 'mock-user-id',
    account_id: 'plaid-credit-acc-id',
    plaidItemId: 'mock-item-id-2',
    transaction_id: 'plaid-tx-id-62',
    name: 'Furniture Store',
    amount: -550.00,
    date: '2024-12-10',
    iso_currency_code: 'USD',
    category: ['SHOPPING', 'Furniture'], // Updated
    pending: false,
    merchant_name: 'IKEA',
    logo_url: 'https://logo.clearbit.com/ikea.com',
    payment_channel: 'in store',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
   {
    id: 'mock-tx-63',
    userId: 'mock-user-id',
    account_id: 'plaid-checking-acc-id',
    plaidItemId: 'mock-item-id-1',
    transaction_id: 'plaid-tx-id-63',
    name: 'Utility Bill - Gas',
    amount: -45.20,
    date: '2024-11-22',
    iso_currency_code: 'USD',
    category: ['RENT_AND_UTILITIES', 'Gas'], // Updated
    pending: false,
    merchant_name: 'City Gas Company',
    logo_url: null,
    payment_channel: 'online',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  // --- END OF NEWLY ADDED MOCK TRANSACTIONS ---

  // --- ADDING 20 MORE RECENT TRANSACTIONS ---
  {
    id: 'mock-tx-64',
    userId: 'mock-user-id',
    account_id: 'plaid-credit-acc-id',
    plaidItemId: 'mock-item-id-2',
    transaction_id: 'plaid-tx-id-64',
    name: 'Uber Ride',
    amount: -22.50,
    date: '2025-04-25',
    iso_currency_code: 'USD',
    category: ['TRAVEL', 'Ride Share'], // Updated
    pending: false,
    merchant_name: 'Uber',
    logo_url: 'https://logo.clearbit.com/uber.com',
    payment_channel: 'online',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'mock-tx-65',
    userId: 'mock-user-id',
    account_id: 'plaid-checking-acc-id',
    plaidItemId: 'mock-item-id-1',
    transaction_id: 'plaid-tx-id-65',
    name: 'Farmers Market',
    amount: -41.30,
    date: '2025-04-22',
    iso_currency_code: 'USD',
    category: ['FOOD_AND_DRINK', 'Groceries'], // Updated
    pending: false,
    merchant_name: 'Local Farmers Market',
    logo_url: null,
    payment_channel: 'in store',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'mock-tx-66',
    userId: 'mock-user-id',
    account_id: 'plaid-credit-acc-id',
    plaidItemId: 'mock-item-id-2',
    transaction_id: 'plaid-tx-id-66',
    name: 'Etsy Purchase',
    amount: -65.00,
    date: '2025-04-19',
    iso_currency_code: 'USD',
    category: ['SHOPPING', 'Arts and Crafts'], // Updated
    pending: false,
    merchant_name: 'Etsy',
    logo_url: 'https://logo.clearbit.com/etsy.com',
    payment_channel: 'online',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'mock-tx-67',
    userId: 'mock-user-id',
    account_id: 'plaid-checking-acc-id',
    plaidItemId: 'mock-item-id-1',
    transaction_id: 'plaid-tx-id-67',
    name: 'Dry Cleaning',
    amount: -35.50,
    date: '2025-04-16',
    iso_currency_code: 'USD',
    category: ['SERVICES', 'Laundry and Dry Cleaning'], // Updated (Mapped General Services -> SERVICES)
    pending: false,
    merchant_name: 'Local Dry Cleaners',
    logo_url: null,
    payment_channel: 'in store',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'mock-tx-68',
    userId: 'mock-user-id',
    account_id: 'plaid-credit-acc-id',
    plaidItemId: 'mock-item-id-2',
    transaction_id: 'plaid-tx-id-68',
    name: 'Streaming Service - Disney+',
    amount: -7.99,
    date: '2025-04-14',
    iso_currency_code: 'USD',
    category: ['SERVICES', 'Subscription'], // Updated
    pending: false,
    merchant_name: 'Disney+',
    logo_url: 'https://logo.clearbit.com/disneyplus.com',
    payment_channel: 'online',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
   {
    id: 'mock-tx-69',
    userId: 'mock-user-id',
    account_id: 'plaid-checking-acc-id',
    plaidItemId: 'mock-item-id-1',
    transaction_id: 'plaid-tx-id-69',
    name: 'Lunch with Coworkers',
    amount: -24.80,
    date: '2025-04-11',
    iso_currency_code: 'USD',
    category: ['FOOD_AND_DRINK', 'Restaurants'], // Updated
    pending: false,
    merchant_name: 'Office Park Cafe',
    logo_url: null,
    payment_channel: 'in store',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'mock-tx-70',
    userId: 'mock-user-id',
    account_id: 'plaid-credit-acc-id',
    plaidItemId: 'mock-item-id-2',
    transaction_id: 'plaid-tx-id-70',
    name: 'Video Game Purchase',
    amount: -59.99,
    date: '2025-04-09',
    iso_currency_code: 'USD',
    category: ['ENTERTAINMENT', 'Video Games'], // Updated
    pending: false,
    merchant_name: 'Steam',
    logo_url: 'https://logo.clearbit.com/steampowered.com',
    payment_channel: 'online',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'mock-tx-71',
    userId: 'mock-user-id',
    account_id: 'plaid-checking-acc-id',
    plaidItemId: 'mock-item-id-1',
    transaction_id: 'plaid-tx-id-71',
    name: 'Parking Ticket',
    amount: -60.00,
    date: '2025-04-06',
    iso_currency_code: 'USD',
    category: ['SERVICES', 'Government Departments and Agencies'], // Updated (Mapped General Services -> SERVICES)
    pending: false,
    merchant_name: 'City Parking Enforcement',
    logo_url: null,
    payment_channel: 'online',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'mock-tx-72',
    userId: 'mock-user-id',
    account_id: 'plaid-credit-acc-id',
    plaidItemId: 'mock-item-id-2',
    transaction_id: 'plaid-tx-id-72',
    name: 'Target Run',
    amount: -135.70,
    date: '2025-04-03',
    iso_currency_code: 'USD',
    category: ['SHOPPING', 'Department Stores'], // Updated
    pending: false,
    merchant_name: 'Target',
    logo_url: 'https://logo.clearbit.com/target.com',
    payment_channel: 'in store',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'mock-tx-73',
    userId: 'mock-user-id',
    account_id: 'plaid-checking-acc-id',
    plaidItemId: 'mock-item-id-1',
    transaction_id: 'plaid-tx-id-73',
    name: 'Haircut',
    amount: -50.00,
    date: '2025-03-29',
    iso_currency_code: 'USD',
    category: ['SERVICES', 'Personal Care'], // Updated (Mapped General Services -> SERVICES)
    pending: false,
    merchant_name: 'Local Barber Shop',
    logo_url: null,
    payment_channel: 'in store',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'mock-tx-74',
    userId: 'mock-user-id',
    account_id: 'plaid-credit-acc-id',
    plaidItemId: 'mock-item-id-2',
    transaction_id: 'plaid-tx-id-74',
    name: 'Online Course',
    amount: -199.00,
    date: '2025-03-26',
    iso_currency_code: 'USD',
    category: ['SERVICES', 'Education'], // Updated
    pending: false,
    merchant_name: 'Udemy',
    logo_url: 'https://logo.clearbit.com/udemy.com',
    payment_channel: 'online',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'mock-tx-75',
    userId: 'mock-user-id',
    account_id: 'plaid-checking-acc-id',
    plaidItemId: 'mock-item-id-1',
    transaction_id: 'plaid-tx-id-75',
    name: 'Takeout Pizza',
    amount: -33.20,
    date: '2025-03-23',
    iso_currency_code: 'USD',
    category: ['FOOD_AND_DRINK', 'Restaurants', 'Pizza'], // Updated
    pending: false,
    merchant_name: 'Dominos',
    logo_url: 'https://logo.clearbit.com/dominos.com',
    payment_channel: 'online',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'mock-tx-76',
    userId: 'mock-user-id',
    account_id: 'plaid-credit-acc-id',
    plaidItemId: 'mock-item-id-2',
    transaction_id: 'plaid-tx-id-76',
    name: 'Gas Station Snacks',
    amount: -12.75,
    date: '2025-03-20',
    iso_currency_code: 'USD',
    category: ['FOOD_AND_DRINK', 'Convenience Stores'], // Updated
    pending: false,
    merchant_name: 'Shell',
    logo_url: 'https://logo.clearbit.com/shell.com',
    payment_channel: 'in store',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'mock-tx-77',
    userId: 'mock-user-id',
    account_id: 'plaid-checking-acc-id',
    plaidItemId: 'mock-item-id-1',
    transaction_id: 'plaid-tx-id-77',
    name: 'Dentist Visit Co-pay',
    amount: -50.00,
    date: '2025-03-17',
    iso_currency_code: 'USD',
    category: ['HEALTHCARE', 'Dentists'], // Updated
    pending: false,
    merchant_name: 'Downtown Dental Care',
    logo_url: null,
    payment_channel: 'in store',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'mock-tx-78',
    userId: 'mock-user-id',
    account_id: 'plaid-credit-acc-id',
    plaidItemId: 'mock-item-id-2',
    transaction_id: 'plaid-tx-id-78',
    name: 'Cloud Storage Subscription - Dropbox',
    amount: -11.99,
    date: '2025-03-14',
    iso_currency_code: 'USD',
    category: ['SERVICES', 'Subscription', 'Software'], // Updated
    pending: false,
    merchant_name: 'Dropbox',
    logo_url: 'https://logo.clearbit.com/dropbox.com',
    payment_channel: 'online',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'mock-tx-79',
    userId: 'mock-user-id',
    account_id: 'plaid-checking-acc-id',
    plaidItemId: 'mock-item-id-1',
    transaction_id: 'plaid-tx-id-79',
    name: 'Local Brewery',
    amount: -48.00,
    date: '2025-03-11',
    iso_currency_code: 'USD',
    category: ['FOOD_AND_DRINK', 'Bars'], // Updated
    pending: false,
    merchant_name: 'Craft Brew House',
    logo_url: null,
    payment_channel: 'in store',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'mock-tx-80',
    userId: 'mock-user-id',
    account_id: 'plaid-credit-acc-id',
    plaidItemId: 'mock-item-id-2',
    transaction_id: 'plaid-tx-id-80',
    name: 'Concert Merchandise',
    amount: -60.00,
    date: '2025-03-08',
    iso_currency_code: 'USD',
    category: ['Entertainment', 'Music'],
    pending: false,
    merchant_name: 'Live Nation Store',
    logo_url: 'https://logo.clearbit.com/livenation.com',
    payment_channel: 'in store',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'mock-tx-81',
    userId: 'mock-user-id',
    account_id: 'plaid-checking-acc-id',
    plaidItemId: 'mock-item-id-1',
    transaction_id: 'plaid-tx-id-81',
    name: 'Bookstore Cafe',
    amount: -15.25,
    date: '2025-03-04',
    iso_currency_code: 'USD',
    category: ['Food and Drink', 'Coffee Shop'],
    pending: false,
    merchant_name: 'Chapters Indigo',
    logo_url: 'https://logo.clearbit.com/chapters.indigo.ca',
    payment_channel: 'in store',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'mock-tx-82',
    userId: 'mock-user-id',
    account_id: 'plaid-credit-acc-id',
    plaidItemId: 'mock-item-id-2',
    transaction_id: 'plaid-tx-id-82',
    name: 'Home Decor Store',
    amount: -95.50,
    date: '2025-03-01',
    iso_currency_code: 'USD',
    category: ['Shops', 'Home Improvement'],
    pending: false,
    merchant_name: 'West Elm',
    logo_url: 'https://logo.clearbit.com/westelm.com',
    payment_channel: 'in store',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'mock-tx-83',
    userId: 'mock-user-id',
    account_id: 'plaid-savings-acc-id', // Savings account
    plaidItemId: 'mock-item-id-1',
    transaction_id: 'plaid-tx-id-83',
    name: 'Transfer from Checking',
    amount: 300.00, // Positive amount
    date: '2025-03-10',
    iso_currency_code: 'USD',
    category: ['Transfer', 'Deposit'],
    pending: false,
    merchant_name: null,
    logo_url: null,
    payment_channel: 'other',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  // --- END OF ADDING 20 MORE RECENT TRANSACTIONS ---

  // --- ADDING 20 MORE TRANSACTIONS WITH EXISTING DATES ---
  {
    id: 'mock-tx-84',
    userId: 'mock-user-id',
    account_id: 'plaid-checking-acc-id',
    plaidItemId: 'mock-item-id-1',
    transaction_id: 'plaid-tx-id-84',
    name: 'Bakery Purchase',
    amount: -12.50,
    date: '2025-04-24', // Existing date
    iso_currency_code: 'USD',
    category: ['Food and Drink', 'Bakeries'],
    pending: false,
    merchant_name: 'Corner Bakery',
    logo_url: null,
    payment_channel: 'in store',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'mock-tx-85',
    userId: 'mock-user-id',
    account_id: 'plaid-credit-acc-id',
    plaidItemId: 'mock-item-id-2',
    transaction_id: 'plaid-tx-id-85',
    name: 'App Store Purchase',
    amount: -4.99,
    date: '2025-04-23', // Existing date
    iso_currency_code: 'USD',
    category: ['Shops', 'Digital Purchases'],
    pending: false,
    merchant_name: 'Apple App Store',
    logo_url: 'https://logo.clearbit.com/apple.com',
    payment_channel: 'online',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'mock-tx-86',
    userId: 'mock-user-id',
    account_id: 'plaid-checking-acc-id',
    plaidItemId: 'mock-item-id-1',
    transaction_id: 'plaid-tx-id-86',
    name: 'Convenience Store',
    amount: -9.80,
    date: '2025-04-20', // Existing date
    iso_currency_code: 'USD',
    category: ['Food and Drink', 'Convenience Stores'],
    pending: false,
    merchant_name: '7-Eleven',
    logo_url: 'https://logo.clearbit.com/7-eleven.com',
    payment_channel: 'in store',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'mock-tx-87',
    userId: 'mock-user-id',
    account_id: 'plaid-credit-acc-id',
    plaidItemId: 'mock-item-id-2',
    transaction_id: 'plaid-tx-id-87',
    name: 'Movie Concessions',
    amount: -18.50,
    date: '2025-04-18', // Existing date
    iso_currency_code: 'USD',
    category: ['Entertainment', 'Movies'],
    pending: false,
    merchant_name: 'Cinema Plex Concessions',
    logo_url: 'https://logo.clearbit.com/amctheatres.com',
    payment_channel: 'in store',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'mock-tx-88',
    userId: 'mock-user-id',
    account_id: 'plaid-checking-acc-id',
    plaidItemId: 'mock-item-id-1',
    transaction_id: 'plaid-tx-id-88',
    name: 'Vending Machine',
    amount: -3.75,
    date: '2025-04-15', // Existing date
    iso_currency_code: 'USD',
    category: ['Food and Drink', 'Vending Machines'],
    pending: false,
    merchant_name: 'Vending Machine',
    logo_url: null,
    payment_channel: 'in store',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'mock-tx-89',
    userId: 'mock-user-id',
    account_id: 'plaid-credit-acc-id',
    plaidItemId: 'mock-item-id-2',
    transaction_id: 'plaid-tx-id-89',
    name: 'Audible Subscription',
    amount: -14.95,
    date: '2025-04-12', // Existing date
    iso_currency_code: 'USD',
    category: ['Service', 'Subscription', 'Books and Supplies'],
    pending: false,
    merchant_name: 'Audible',
    logo_url: 'https://logo.clearbit.com/audible.com',
    payment_channel: 'online',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
   {
    id: 'mock-tx-90',
    userId: 'mock-user-id',
    account_id: 'plaid-checking-acc-id',
    plaidItemId: 'mock-item-id-1',
    transaction_id: 'plaid-tx-id-90',
    name: 'ATM Fee',
    amount: -3.00,
    date: '2025-04-10', // Existing date
    iso_currency_code: 'USD',
    category: ['General Services', 'Bank Fees', 'ATM'],
    pending: false,
    merchant_name: 'ATM Network Fee',
    logo_url: null,
    payment_channel: 'other',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'mock-tx-91',
    userId: 'mock-user-id',
    account_id: 'plaid-credit-acc-id',
    plaidItemId: 'mock-item-id-2',
    transaction_id: 'plaid-tx-id-91',
    name: 'Dessert Place',
    amount: -22.00,
    date: '2025-04-08', // Existing date
    iso_currency_code: 'USD',
    category: ['Food and Drink', 'Restaurants', 'Desserts'],
    pending: false,
    merchant_name: 'Sweet Treats',
    logo_url: null,
    payment_channel: 'in store',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'mock-tx-92',
    userId: 'mock-user-id',
    account_id: 'plaid-checking-acc-id',
    plaidItemId: 'mock-item-id-1',
    transaction_id: 'plaid-tx-id-92',
    name: 'Cable Bill Add-on',
    amount: -15.00,
    date: '2025-04-05', // Existing date
    iso_currency_code: 'USD',
    category: ['Rent and Utilities', 'Television'],
    pending: false,
    merchant_name: 'Comcast',
    logo_url: 'https://logo.clearbit.com/comcast.com',
    payment_channel: 'online',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'mock-tx-93',
    userId: 'mock-user-id',
    account_id: 'plaid-credit-acc-id',
    plaidItemId: 'mock-item-id-2',
    transaction_id: 'plaid-tx-id-93',
    name: 'Gardening Supplies',
    amount: -45.50,
    date: '2025-04-02', // Existing date
    iso_currency_code: 'USD',
    category: ['Shops', 'Home Improvement'],
    pending: false,
    merchant_name: 'Home Depot',
    logo_url: 'https://logo.clearbit.com/homedepot.com',
    payment_channel: 'in store',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'mock-tx-94',
    userId: 'mock-user-id',
    account_id: 'plaid-checking-acc-id',
    plaidItemId: 'mock-item-id-1',
    transaction_id: 'plaid-tx-id-94',
    name: 'Car Wash Upgrade',
    amount: -5.00,
    date: '2025-02-28', // Existing date
    iso_currency_code: 'USD',
    category: ['General Services', 'Automotive'],
    pending: false,
    merchant_name: 'Speedy Car Wash',
    logo_url: null,
    payment_channel: 'in store',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'mock-tx-95',
    userId: 'mock-user-id',
    account_id: 'plaid-credit-acc-id',
    plaidItemId: 'mock-item-id-2',
    transaction_id: 'plaid-tx-id-95',
    name: 'Sports Apparel',
    amount: -40.00,
    date: '2025-02-25', // Existing date
    iso_currency_code: 'USD',
    category: ['Shops', 'Sporting Goods'],
    pending: false,
    merchant_name: 'DICK\'S Sporting Goods',
    logo_url: 'https://logo.clearbit.com/dickssportinggoods.com',
    payment_channel: 'in store',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'mock-tx-96',
    userId: 'mock-user-id',
    account_id: 'plaid-checking-acc-id',
    plaidItemId: 'mock-item-id-1',
    transaction_id: 'plaid-tx-id-96',
    name: 'Lunch Sandwich',
    amount: -11.95,
    date: '2025-02-15', // Existing date
    iso_currency_code: 'USD',
    category: ['Food and Drink', 'Restaurants', 'Fast Food'],
    pending: false,
    merchant_name: 'Quick Eats Deli',
    logo_url: null,
    payment_channel: 'in store',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'mock-tx-97',
    userId: 'mock-user-id',
    account_id: 'plaid-credit-acc-id',
    plaidItemId: 'mock-item-id-2',
    transaction_id: 'plaid-tx-id-97',
    name: 'Electronics Accessory',
    amount: -29.99,
    date: '2025-02-12', // Existing date
    iso_currency_code: 'USD',
    category: ['Shops', 'Electronics'],
    pending: false,
    merchant_name: 'Best Buy',
    logo_url: 'https://logo.clearbit.com/bestbuy.com',
    payment_channel: 'in store',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'mock-tx-98',
    userId: 'mock-user-id',
    account_id: 'plaid-checking-acc-id',
    plaidItemId: 'mock-item-id-1',
    transaction_id: 'plaid-tx-id-98',
    name: 'Grocery - Dairy',
    amount: -21.50,
    date: '2025-02-10', // Existing date
    iso_currency_code: 'USD',
    category: ['Food and Drink', 'Groceries'],
    pending: false,
    merchant_name: 'Safeway',
    logo_url: 'https://logo.clearbit.com/safeway.com',
    payment_channel: 'in store',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'mock-tx-99',
    userId: 'mock-user-id',
    account_id: 'plaid-credit-acc-id',
    plaidItemId: 'mock-item-id-2',
    transaction_id: 'plaid-tx-id-99',
    name: 'Department Store - Cosmetics',
    amount: -38.20,
    date: '2025-02-02', // Existing date
    iso_currency_code: 'USD',
    category: ['Shops', 'Department Stores', 'Personal Care'],
    pending: false,
    merchant_name: 'Macy\'s',
    logo_url: 'https://logo.clearbit.com/macys.com',
    payment_channel: 'in store',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'mock-tx-100',
    userId: 'mock-user-id',
    account_id: 'plaid-checking-acc-id',
    plaidItemId: 'mock-item-id-1',
    transaction_id: 'plaid-tx-id-100',
    name: 'Gas Station Drink',
    amount: -2.50,
    date: '2025-01-28', // Existing date
    iso_currency_code: 'USD',
    category: ['Travel', 'Gas Stations'],
    pending: false,
    merchant_name: 'ExxonMobil',
    logo_url: 'https://logo.clearbit.com/exxonmobil.com',
    payment_channel: 'in store',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'mock-tx-101',
    userId: 'mock-user-id',
    account_id: 'plaid-credit-acc-id',
    plaidItemId: 'mock-item-id-2',
    transaction_id: 'plaid-tx-id-101',
    name: 'Restaurant Appetizer',
    amount: -15.00,
    date: '2025-01-22', // Existing date
    iso_currency_code: 'USD',
    category: ['Food and Drink', 'Restaurants'],
    pending: false,
    merchant_name: 'Neighborhood Grill',
    logo_url: null,
    payment_channel: 'in store',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'mock-tx-102',
    userId: 'mock-user-id',
    account_id: 'plaid-checking-acc-id',
    plaidItemId: 'mock-item-id-1',
    transaction_id: 'plaid-tx-id-102',
    name: 'Hardware Store - Tools',
    amount: -28.60,
    date: '2025-01-15', // Existing date
    iso_currency_code: 'USD',
    category: ['Shops', 'Hardware Store'],
    pending: false,
    merchant_name: 'Ace Hardware',
    logo_url: 'https://logo.clearbit.com/acehardware.com',
    payment_channel: 'in store',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'mock-tx-103',
    userId: 'mock-user-id',
    account_id: 'plaid-credit-acc-id',
    plaidItemId: 'mock-item-id-2',
    transaction_id: 'plaid-tx-id-103',
    name: 'Amazon - Household Item',
    amount: -24.95,
    date: '2025-01-07', // Existing date
    iso_currency_code: 'USD',
    category: ['Shops', 'Digital Purchases'],
    pending: false,
    merchant_name: 'Amazon',
    logo_url: 'https://logo.clearbit.com/amazon.com',
    payment_channel: 'online',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },

  // --- ADDING MORE TRANSACTIONS FOR STARBUCKS AT SAME LOCATION (45.4210, -75.6980) ---
  {
    id: 'mock-tx-104',
    userId: 'mock-user-id',
    account_id: 'plaid-credit-acc-id',
    plaidItemId: 'mock-item-id-2',
    transaction_id: 'plaid-tx-id-104',
    name: 'Coffee Shop',
    amount: -6.25,
    date: '2025-01-15', // Different date
    iso_currency_code: 'USD',
    category: ['FOOD_AND_DRINK', 'Coffee Shop'], 
    pending: false,
    merchant_name: 'Starbucks',
    logo_url: 'https://logo.clearbit.com/starbucks.com',
    location: { lat: 45.4210, lon: -75.6980, address: null, city: 'Ottawa', region: 'ON', postal_code: null, country: 'CA', store_number: null }, // Same location
    payment_channel: 'in store',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'mock-tx-105',
    userId: 'mock-user-id',
    account_id: 'plaid-checking-acc-id',
    plaidItemId: 'mock-item-id-1',
    transaction_id: 'plaid-tx-id-105',
    name: 'Morning Coffee',
    amount: -4.80,
    date: '2025-02-10', // Different date
    iso_currency_code: 'USD',
    category: ['FOOD_AND_DRINK', 'Coffee Shop'], 
    pending: false,
    merchant_name: 'Starbucks',
    logo_url: 'https://logo.clearbit.com/starbucks.com',
    location: { lat: 45.4210, lon: -75.6980, address: null, city: 'Ottawa', region: 'ON', postal_code: null, country: 'CA', store_number: null }, // Same location
    payment_channel: 'in store',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  // --- END OF ADDED STARBUCKS TRANSACTIONS ---

  {
    id: 'mock-tx-54',
    userId: 'mock-user-id',
    account_id: 'plaid-checking-acc-id',
    plaidItemId: 'mock-item-id-1',
    transaction_id: 'plaid-tx-id-54',
    name: 'Holiday Gifts',
    amount: -150.00,
    date: '2024-12-20',
    iso_currency_code: 'USD',
    category: ['SHOPPING', 'Gifts'], // Updated
    pending: false,
    merchant_name: 'Various Retailers',
    logo_url: null,
    payment_channel: 'in store',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
];

// --- MOCK BANK ACCOUNT DATA ---
export const mockBankAccounts: BankAccount[] = [
  {
    id: "mock-checking-123",
    plaidItemId: "mock-item-id-1",
    plaidAccountId: "plaid-checking-acc-id",
    name: "Primary Checking",
    mask: "1111",
    type: "depository",
    balance: 1250.75,
    availableBalance: 1200.50,
    currency: "USD",
    createdAt: new Date(),
    updatedAt: new Date(),
    limit: undefined,
  },
  {
    id: "mock-savings-456",
    plaidItemId: "mock-item-id-1",
    plaidAccountId: "plaid-savings-acc-id",
    name: "High-Yield Savings",
    mask: "2222",
    type: "depository",
    balance: 5500.00,
    availableBalance: 5500.00,
    currency: "USD",
    createdAt: new Date(),
    updatedAt: new Date(),
    limit: undefined,
  },
  {
    id: "mock-credit-789",
    plaidItemId: "mock-item-id-2",
    plaidAccountId: "plaid-credit-acc-id",
    name: "Travel Rewards Card",
    mask: "3333",
    type: "credit",
    balance: -875.30,
    availableBalance: 9124.70,
    currency: "USD",
    createdAt: new Date(),
    updatedAt: new Date(),
    limit: 10000,
  },
    {
    id: "mock-loan-101",
    plaidItemId: "mock-item-id-3",
    plaidAccountId: "plaid-loan-acc-id",
    name: "Student Loan",
    mask: "4444",
    type: "loan",
    balance: -15200.00,
    availableBalance: undefined,
    currency: "USD",
    createdAt: new Date(),
    updatedAt: new Date(),
    limit: undefined,
  },
];

// --- MOCK PLAID ITEMS ---
export const mockPlaidItems: MockPlaidItem[] = [
 {
   id: "db-item-id-1",
   plaidItemId: "mock-item-id-1",
   institutionName: "Royal Bank of Canada",
   userId: "mock-user-id",
   accessToken: "mock-token-1",
   institutionId: "ins_rbc",
   createdAt: new Date(),
   updatedAt: new Date(),
   logoUrl: "https://seeklogo.com/images/R/rbc-royal-bank-of-canada-logo-D0A1D244CF-seeklogo.com.png"
 },
 {
   id: "db-item-id-2",
   plaidItemId: "mock-item-id-2",
   institutionName: "TD Canada Trust",
   userId: "mock-user-id",
   accessToken: "mock-token-2",
   institutionId: "ins_td",
   createdAt: new Date(),
   updatedAt: new Date(),
   logoUrl: "https://upload.wikimedia.org/wikipedia/commons/a/a4/Toronto-Dominion_Bank_logo.svg"
 },
 {
   id: "db-item-id-3",
   plaidItemId: "mock-item-id-3",
   institutionName: "Scotiabank",
   userId: "mock-user-id",
   accessToken: "mock-token-3",
   institutionId: "ins_scotia",
   createdAt: new Date(),
   updatedAt: new Date(),
   logoUrl: "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcSS0o75dau5e5eaV5qOJBbVBsjyVQ3NbQS8nA&s"
 },
];

// --- MOCK DASHBOARD BUDGET DATA ---
export const dashboardBudgetData: BudgetCategoryData[] = [
  {
    id: "needs",
    name: "Needs",
    icon: "🏠",
    allocated: 2000,
    spent: 1850,
    color: '#3498DB'
  },
  {
    id: "wants",
    name: "Wants",
    icon: "🛍️",
    allocated: 800,
    spent: 670,
    color: '#FFBF00'
  },
  {
    id: "travel",
    name: "Travel",
    icon: "✈️",
    allocated: 300,
    spent: 350,
    color: '#E74C3C'
  },
  {
    id: "mustang",
    name: "Lovely Mustang 69",
    icon: "🚗",
    allocated: 500,
    spent: 150,
  },
  {
    id: "savings",
    name: "Savings",
    icon: "💰",
    allocated: 1000,
    spent: 900,
    color: '#2ECC71'
  },
]; 